<template>
  <div class="mt-8">
    <v-card>
      <v-card-title>Firebase Connection Test</v-card-title>
      <v-card-text>
        <v-alert
          :type="connectionStatus.type"
          :icon="connectionStatus.icon"
          prominent
        >
          {{ connectionStatus.message }}
        </v-alert>
        
        <v-divider class="my-4"></v-divider>
        
        <v-btn
          color="primary"
          @click="testConnection"
          :loading="testing"
          class="mr-2"
        >
          Test Connection
        </v-btn>
        
        <v-btn
          color="secondary"
          @click="loadTrackingData"
          :loading="loadingData"
        >
          Load Tracking Data
        </v-btn>
        
        <v-btn
          color="info"
          @click="showDebugInfo = !showDebugInfo"
          class="ml-2"
        >
          {{ showDebugInfo ? 'Hide' : 'Show' }} Debug Info
        </v-btn>
        
        <v-btn
          color="warning"
          @click="testRestAPI"
          :loading="testingRest"
          class="ml-2"
        >
          Test REST API
        </v-btn>
        
        <v-btn
          color="info"
          @click="testNetworkStatus"
          :loading="testingNetwork"
          class="ml-2"
        >
          Test Network
        </v-btn>
        
        <v-btn
          color="error"
          @click="simpleFirebaseTest"
          :loading="testingSimple"
          class="ml-2"
        >
          Simple Firebase Test
        </v-btn>
        
        <v-btn
          color="warning"
          @click="testDatabaseConfig"
          :loading="testingConfig"
          class="ml-2"
        >
          Test Database Config
        </v-btn>
        
        <v-btn
          color="info"
          @click="compareSDKvsREST"
          :loading="testingRest"
          class="ml-2"
        >
          Compare SDK vs REST
        </v-btn>
        
        <v-btn
          color="success"
          @click="testExactDataComparison"
          :loading="testingExact"
          class="ml-2"
        >
          Test Exact Data
        </v-btn>
        
        <v-btn
          color="warning"
          @click="testSDKWithDifferentConfigs"
          :loading="testingExact"
          class="ml-2"
        >
          Test SDK Configs
        </v-btn>
        
        <v-btn
          color="info"
          @click="testRESTvsSDKComparison"
          :loading="testingExact"
          class="ml-2"
        >
          REST vs SDK Test
        </v-btn>
        
        <v-btn
          color="secondary"
          @click="clearResults"
          class="ml-2"
        >
          Clear Results
        </v-btn>
        
        <v-divider class="my-4"></v-divider>
        
        <!-- Debug Info Section -->
        <div v-if="showDebugInfo">
          <v-card outlined class="mb-4">
            <v-card-title class="text-h6">
              <v-icon left>mdi-bug</v-icon>
              Debug Information
            </v-card-title>
            <v-card-text>
              <!-- Status Summary -->
              <v-row class="mb-4">
                <v-col cols="12" md="6">
                  <v-card outlined class="pa-3">
                    <div class="text-h6 mb-2">Connection Status</div>
                    <v-list dense>
                      <v-list-item>
                        <v-list-item-icon>
                          <v-icon :color="connectionStatus.type === 'success' ? 'success' : connectionStatus.type === 'error' ? 'error' : 'warning'">
                            {{ connectionStatus.type === 'success' ? 'mdi-check-circle' : connectionStatus.type === 'error' ? 'mdi-close-circle' : 'mdi-alert' }}
                          </v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title>{{ connectionStatus.message }}</v-list-item-title>
                          <v-list-item-subtitle>Last updated: {{ new Date().toLocaleTimeString() }}</v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-card>
                </v-col>
                
                <v-col cols="12" md="6">
                  <v-card outlined class="pa-3">
                    <div class="text-h6 mb-2">Data Status</div>
                    <v-list dense>
                      <v-list-item>
                        <v-list-item-icon>
                          <v-icon :color="trackingData.length > 0 ? 'success' : 'warning'">
                            {{ trackingData.length > 0 ? 'mdi-database-check' : 'mdi-database-remove' }}
                          </v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title>{{ trackingData.length }} Records Found</v-list-item-title>
                          <v-list-item-subtitle>{{ trackingData.length > 0 ? 'Data loaded successfully' : 'No data available' }}</v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-card>
                </v-col>
              </v-row>
              
              <v-list dense>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-cog</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Project ID</v-list-item-title>
                    <v-list-item-subtitle>{{ debugInfo.projectId || 'Loading...' }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-database</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Database Status</v-list-item-title>
                    <v-list-item-subtitle>{{ debugInfo.dbStatus || 'Unknown' }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-collection</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Collection Name</v-list-item-title>
                    <v-list-item-subtitle>tracking_driver</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-database</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Database Name</v-list-item-title>
                    <v-list-item-subtitle>megavin (non-default)</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-clock</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Last Test Time</v-list-item-title>
                    <v-list-item-subtitle>{{ debugInfo.lastTestTime || 'Not tested yet' }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-information</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Environment</v-list-item-title>
                    <v-list-item-subtitle>{{ nodeEnv }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-web</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Current URL</v-list-item-title>
                    <v-list-item-subtitle>{{ window.location.href }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-database</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Firebase SDK Version</v-list-item-title>
                    <v-list-item-subtitle>{{ debugInfo.firebaseVersion || 'Checking...' }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-connection</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Connection Status</v-list-item-title>
                    <v-list-item-subtitle>
                      <v-chip 
                        :color="connectionStatus.type === 'success' ? 'success' : connectionStatus.type === 'error' ? 'error' : 'warning'" 
                        small
                      >
                        {{ connectionStatus.type.toUpperCase() }}
                      </v-chip>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-data-matrix</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Data Status</v-list-item-title>
                    <v-list-item-subtitle>
                      <v-chip 
                        :color="trackingData.length > 0 ? 'success' : 'warning'" 
                        small
                      >
                        {{ trackingData.length > 0 ? `${trackingData.length} Records` : 'No Data' }}
                      </v-chip>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
          
          <!-- Help Section -->
          <v-card outlined class="mb-4">
            <v-card-title class="text-h6">
              <v-icon left>mdi-help-circle</v-icon>
              Troubleshooting Guide
            </v-card-title>
            <v-card-text>
              <v-alert type="warning" prominent>
                <strong>Connection Issues Detected</strong><br>
                If you're getting "unavailable" or connection errors, try these solutions:
              </v-alert>
              
              <v-list dense>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-wifi</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Check Network Connection</v-list-item-title>
                    <v-list-item-subtitle>Ensure you have a stable internet connection</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-alert-circle</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>CORS Issues (Development)</v-list-item-title>
                    <v-list-item-subtitle>CORS errors are normal in development. This doesn't affect Firebase SDK.</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-firebase</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Check Firebase Project Status</v-list-item-title>
                    <v-list-item-subtitle>
                      <a href="https://status.firebase.google.com/" target="_blank">
                        Firebase Status Page
                      </a>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-cog</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Verify Project Configuration</v-list-item-title>
                    <v-list-item-subtitle>Ensure project ID "megavin-2024" and database "megavin" are correct</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-shield-check</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Check Security Rules</v-list-item-title>
                    <v-list-item-subtitle>Ensure Firestore rules allow read access and anonymous authentication is enabled</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-refresh</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Use REST API as Fallback</v-list-item-title>
                    <v-list-item-subtitle>REST API often works when SDK fails</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-database</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Check Collection Exists</v-list-item-title>
                    <v-list-item-subtitle>Verify "tracking_driver" collection exists in Firestore</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-file-document</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Check Documents Exist</v-list-item-title>
                    <v-list-item-subtitle>Ensure there are documents in the tracking_driver collection</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
              
              <v-divider class="my-4"></v-divider>
              
              <v-alert type="info" prominent>
                <strong>About CORS Errors</strong><br>
                CORS errors in development are normal and don't affect Firebase SDK functionality. 
                The SDK uses different connection methods that bypass CORS restrictions.
              </v-alert>
              
              <v-alert type="success" prominent>
                <strong>Recommended Solution</strong><br>
                Use "Test REST API" button - it bypasses SDK issues and directly accesses Firebase data.
              </v-alert>
              
              <v-alert type="warning" prominent>
                <strong>If No Data Shows:</strong><br>
                1. Check if "tracking_driver" collection exists in Firebase Console<br>
                2. Verify there are documents in the collection<br>
                3. Check Firestore security rules allow read access<br>
                4. Try the "Simple Firebase Test" button for basic connectivity<br>
                5. Use "Test REST API" as a fallback method
              </v-alert>
              
              <v-alert type="info" prominent>
                <strong>Quick Checks:</strong><br>
                • Click "Simple Firebase Test" to check basic connectivity<br>
                • Click "Test REST API" to bypass SDK issues<br>
                • Check browser console (F12) for detailed error messages<br>
                • Verify Firebase project ID is "megavin-2024"<br>
                • Verify database name is "megavin" (non-default)<br>
                • Ensure Anonymous Authentication is enabled in Firebase Console<br>
                • Ensure you're connected to the internet
              </v-alert>
              
              <v-alert type="warning" prominent>
                <strong>Step-by-Step Troubleshooting:</strong><br>
                1. <strong>Test Basic Connection:</strong> Click "Simple Firebase Test"<br>
                2. <strong>Check REST API:</strong> Click "Test REST API" as fallback<br>
                3. <strong>Verify Firebase Console:</strong> Check if collection exists<br>
                4. <strong>Check Security Rules:</strong> Ensure read access is allowed<br>
                5. <strong>Check Network:</strong> Ensure stable internet connection<br>
                6. <strong>Check Console Logs:</strong> Press F12 for detailed errors
              </v-alert>
              
              <v-alert type="error" prominent>
                <strong>Common Issues & Solutions:</strong><br>
                • <strong>"Permission denied":</strong> Check Firestore security rules<br>
                • <strong>"Service unavailable":</strong> Check network connection<br>
                • <strong>"No documents found":</strong> Collection might be empty<br>
                • <strong>"Collection doesn't exist":</strong> Create collection in Firebase Console<br>
                • <strong>"CORS errors":</strong> Normal in development, doesn't affect SDK<br>
                • <strong>"Timeout errors":</strong> Network or Firebase service issue<br>
                • <strong>"Authentication failed":</strong> Enable Anonymous Authentication in Firebase Console<br>
                • <strong>"Non-default database":</strong> Requires authentication for SDK access
              </v-alert>
              
              <v-divider class="my-4"></v-divider>
              
              <v-list dense>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-firebase</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Firebase Console</v-list-item-title>
                    <v-list-item-subtitle>
                      <a href="https://console.firebase.google.com/project/megavin-2024/firestore/data" target="_blank">
                        Open Firestore Database
                      </a>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-shield-check</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Security Rules</v-list-item-title>
                    <v-list-item-subtitle>
                      <a href="https://console.firebase.google.com/project/megavin-2024/firestore/rules" target="_blank">
                        Check Firestore Rules
                      </a>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-help-circle</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Firebase Documentation</v-list-item-title>
                    <v-list-item-subtitle>
                      <a href="https://firebase.google.com/docs/firestore" target="_blank">
                        Firestore Documentation
                      </a>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </div>
        
        <!-- Console Logs Section -->
        <div v-if="showDebugInfo">
          <v-card outlined class="mb-4">
            <v-card-title class="text-h6">
              <v-icon left>mdi-console</v-icon>
              Console Logs
            </v-card-title>
            <v-card-text>
              <v-alert type="info" prominent>
                <strong>Debugging Tips:</strong><br>
                1. Open browser console (F12) to see detailed logs<br>
                2. Check for any error messages in red<br>
                3. Look for "✅" success messages and "❌" error messages<br>
                4. If you see "No documents found", the collection might be empty<br>
                5. If you see "Permission denied", check Firebase security rules
              </v-alert>
              
              <v-list dense>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-bug</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Browser Console</v-list-item-title>
                    <v-list-item-subtitle>Press F12 and check Console tab for detailed logs</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-firebase</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Firebase Console</v-list-item-title>
                    <v-list-item-subtitle>
                      <a href="https://console.firebase.google.com/project/megavin-2024/firestore/data" target="_blank">
                        Check Firestore Database
                      </a>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-shield-check</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Security Rules</v-list-item-title>
                    <v-list-item-subtitle>
                      <a href="https://console.firebase.google.com/project/megavin-2024/firestore/rules" target="_blank">
                        Check Firestore Security Rules
                      </a>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </div>
        
        <div v-if="testResults.length > 0">
          <h3>Test Results:</h3>
          <v-list dense>
            <v-list-item
              v-for="(result, index) in testResults"
              :key="index"
            >
              <v-list-item-icon>
                <v-icon :color="result.success ? 'success' : 'error'">
                  {{ result.success ? 'mdi-check' : 'mdi-close' }}
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ result.title }}</v-list-item-title>
                <v-list-item-subtitle>{{ result.message }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          
          <!-- Summary Section -->
          <v-card outlined class="mt-4">
            <v-card-title class="text-h6">
              <v-icon left>mdi-chart-line</v-icon>
              Test Summary
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="6">
                  <v-card outlined class="text-center pa-4">
                    <v-icon large color="info">mdi-test-tube</v-icon>
                    <div class="text-h4 font-weight-bold">{{ testResults.length }}</div>
                    <div class="text-caption">Total Tests</div>
                  </v-card>
                </v-col>
                <v-col cols="6">
                  <v-card outlined class="text-center pa-4">
                    <v-icon large color="success">mdi-check-circle</v-icon>
                    <div class="text-h4 font-weight-bold">{{ testResults.filter(r => r.success).length }}</div>
                    <div class="text-caption">Passed</div>
                  </v-card>
                </v-col>
                <v-col cols="6">
                  <v-card outlined class="text-center pa-4">
                    <v-icon large color="error">mdi-close-circle</v-icon>
                    <div class="text-h4 font-weight-bold">{{ testResults.filter(r => !r.success).length }}</div>
                    <div class="text-caption">Failed</div>
                  </v-card>
                </v-col>
                <v-col cols="6">
                  <v-card outlined class="text-center pa-4">
                    <v-icon large color="primary">mdi-percent</v-icon>
                    <div class="text-h4 font-weight-bold">{{ Math.round((testResults.filter(r => r.success).length / testResults.length) * 100) }}%</div>
                    <div class="text-caption">Success Rate</div>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </div>

        <!-- Data Table untuk Tracking Driver -->
        <v-divider class="my-4"></v-divider>
        
        <div v-if="trackingData.length > 0">
          <h3>Tracking Driver Data:</h3>
          <v-alert type="success" prominent>
            <strong>Data Found!</strong> Successfully loaded {{ trackingData.length }} driver records from Firebase.
          </v-alert>
          
          <v-data-table
            :headers="headers"
            :items="trackingData"
            :items-per-page="10"
            class="elevation-1"
          >
            <template v-slot:item.driver_name="{ item }">
              <v-chip color="primary" small>
                {{ item.driver_name }}
              </v-chip>
            </template>
            
            <template v-slot:item.location="{ item }">
              <v-chip color="info" small>
                {{ item.latitude }}, {{ item.longitude }}
              </v-chip>
            </template>
            
            <template v-slot:item.create_datetime="{ item }">
              <span class="text-caption">{{ item.create_datetime }}</span>
            </template>
            
            <template v-slot:item.actions="{ item }">
              <v-btn
                icon
                small
                color="primary"
                @click="viewDriverDetails(item)"
              >
                <v-icon>mdi-eye</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </div>
        
        <div v-else-if="testResults.length > 0">
          <v-alert type="warning" prominent>
            <strong>No Data Found</strong> The tests completed but no tracking data was loaded. 
            Check the test results above for more details.
          </v-alert>
        </div>

        <!-- Driver Details Dialog -->
        <v-dialog v-model="showDriverDialog" max-width="500px">
          <v-card>
            <v-card-title>
              Driver Details - {{ selectedDriver.driver_name }}
            </v-card-title>
            <v-card-text>
              <v-list dense>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-account</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Driver ID</v-list-item-title>
                    <v-list-item-subtitle>{{ selectedDriver.driver_id }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-map-marker</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Location</v-list-item-title>
                    <v-list-item-subtitle>
                      Lat: {{ selectedDriver.latitude }}, Lng: {{ selectedDriver.longitude }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-clock</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Last Update</v-list-item-title>
                    <v-list-item-subtitle>{{ selectedDriver.create_datetime }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="primary" text @click="showDriverDialog = false">
                Close
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import Firebase from '@/plugins/Firebase.js'
import FirebaseHelper from '@/plugins/FirebaseHelper.js'

export default {
  name: 'FirebaseTest',
  data() {
    return {
      testing: false,
      loadingData: false,
      testingRest: false,
      testingNetwork: false,
      testingSimple: false,
      testingConfig: false, // Added for database config test
      testingExact: false, // Added for exact data comparison
      connectionStatus: {
        type: 'info',
        icon: 'mdi-information',
        message: 'Click "Test Connection" to check Firebase status'
      },
      testResults: [],
      trackingData: [],
      headers: [
        { text: 'Driver Name', value: 'driver_name', sortable: true },
        { text: 'Driver ID', value: 'driver_id', sortable: true },
        { text: 'Location', value: 'location', sortable: false },
        { text: 'Last Update', value: 'create_datetime', sortable: true },
        { text: 'Actions', value: 'actions', sortable: false }
      ],
      showDriverDialog: false,
      selectedDriver: {},
      showDebugInfo: false,
      debugInfo: {
        projectId: 'Loading...',
        dbStatus: 'Unknown',
        lastTestTime: 'Not tested yet',
        firebaseVersion: 'Checking...'
      }
    }
  },
  computed: {
    nodeEnv() {
      return process.env.NODE_ENV || 'development'
    }
  },
  async created() {
    // Initialize Firebase Helper when component loads
    await this.initializeFirebaseHelper()
    this.initializeDebugInfo()
  },
  methods: {
    async initializeFirebaseHelper() {
      try {
        console.log('🔧 Initializing Firebase Helper...')
        const result = await FirebaseHelper.initialize()
        
        if (result.success) {
          console.log('✅ Firebase Helper initialized successfully')
          this.addTestResult('Firebase Helper Init', true, result.message)
        } else {
          console.log('❌ Firebase Helper initialization failed:', result.error)
          this.addTestResult('Firebase Helper Init', false, result.error)
        }
      } catch (error) {
        console.error('❌ Error initializing Firebase Helper:', error)
        this.addTestResult('Firebase Helper Init', false, error.message)
      }
    },
    
    async initializeDebugInfo() {
      try {
        const { firebaseApp } = Firebase
        this.debugInfo.projectId = firebaseApp.options.projectId
        this.debugInfo.dbStatus = 'Initialized'
        
        // Get connection status from helper
        const status = FirebaseHelper.getConnectionStatus()
        this.debugInfo.dbStatus = status.connectionStatus
        
        // Try to get Firebase version
        try {
          const { version } = await import('firebase/app')
          this.debugInfo.firebaseVersion = version
        } catch (versionError) {
          this.debugInfo.firebaseVersion = 'Unknown'
        }
        
        // Add more detailed Firebase config info
        console.log('🔧 Firebase Configuration Details:')
        console.log('Project ID:', firebaseApp.options.projectId)
        console.log('Auth Domain:', firebaseApp.options.authDomain)
        console.log('Storage Bucket:', firebaseApp.options.storageBucket)
        console.log('Messaging Sender ID:', firebaseApp.options.messagingSenderId)
        console.log('App ID:', firebaseApp.options.appId)
        console.log('Firebase Version:', this.debugInfo.firebaseVersion)
        console.log('Database Name: megavin (non-default)')
        
        // Check if we can access Firestore
        try {
          const { db } = Firebase
          if (db) {
            console.log('✅ Firestore db is available')
            console.log('📊 Database name: megavin')
            this.debugInfo.dbStatus = 'Available (megavin)'
          } else {
            console.log('❌ Firestore db is not available')
            this.debugInfo.dbStatus = 'Not Available'
          }
        } catch (dbError) {
          console.error('❌ Error accessing Firestore db:', dbError)
          this.debugInfo.dbStatus = 'Error: ' + dbError.message
        }
        
      } catch (error) {
        this.debugInfo.projectId = 'Error'
        this.debugInfo.dbStatus = 'Error: ' + error.message
      }
    },
    
    async checkFirebaseConfig() {
      console.log('🔧 Checking Firebase configuration...')
      
      try {
        const { db } = Firebase
        console.log('📡 Firebase db:', db)
        
        // Check if we can access the app
        const { firebaseApp } = Firebase
        console.log('🔥 Firebase app:', firebaseApp)
        
        // Check app options
        console.log('⚙️ App options:', firebaseApp.options)
        
        // Check if we can get the project ID
        const projectId = firebaseApp.options.projectId
        console.log('🏗️ Project ID:', projectId)
        
        // Update debug info
        this.debugInfo.projectId = projectId
        this.debugInfo.dbStatus = db ? 'Available' : 'Not Available'
        this.debugInfo.lastTestTime = new Date().toLocaleString()
        
        return {
          success: true,
          projectId: projectId,
          message: `Connected to project: ${projectId}`
        }
      } catch (error) {
        console.error('❌ Firebase config check failed:', error)
        this.debugInfo.dbStatus = 'Error: ' + error.message
        return {
          success: false,
          error: error.message
        }
      }
    },
    
    async testConnection() {
      this.testing = true
      this.testResults = []
      
      try {
        console.log('🧪 Starting Firebase connection test...')
        
        // Test 0: Check network connectivity first
        console.log('🌐 Checking network connectivity...')
        try {
          // Test Firebase connectivity instead of external URL to avoid CORS
          const firebaseUrl = 'https://firestore.googleapis.com/v1/projects/megavin-2024/databases/megavin/documents/tracking_driver'
          const networkResponse = await fetch(firebaseUrl)
          if (networkResponse.ok) {
            this.addTestResult('Network Connectivity', true, 'Firebase connectivity OK')
            console.log('✅ Firebase connectivity OK')
          } else {
            this.addTestResult('Network Connectivity', false, `Firebase connectivity issue: ${networkResponse.status}`)
            console.log('⚠️ Firebase connectivity issue')
          }
        } catch (networkError) {
          // CORS errors are expected in development, but we can still detect network
          if (networkError.message.includes('CORS') || networkError.message.includes('Failed to fetch')) {
            this.addTestResult('Network Connectivity', true, 'Network detected (CORS may block external requests)')
            console.log('✅ Network detected (CORS blocked external request)')
          } else {
            this.addTestResult('Network Connectivity', false, `Network error: ${networkError.message}`)
            console.log('❌ Network connectivity failed')
          }
        }
        
        // Test 0.5: Check Firebase configuration
        const configCheck = await this.checkFirebaseConfig()
        if (configCheck.success) {
          this.addTestResult('Firebase Config', true, configCheck.message)
        } else {
          this.addTestResult('Firebase Config', false, `Config error: ${configCheck.error}`)
        }
        
        // Test 1: Check if Firebase is available
        this.addTestResult('Firebase Import', true, 'Firebase module imported successfully')
        console.log('✅ Firebase module imported')
        
        const { db, setupFirebaseEmulator, authenticateAnonymously, checkAuthStatus } = Firebase
        console.log('📡 Firebase db object:', db)
        console.log('📡 Firebase db type:', typeof db)
        
        if (!db) {
          this.addTestResult('Firebase DB', false, 'Firebase db is not available')
          console.error('❌ Firebase db is null or undefined')
          return
        }
        this.addTestResult('Firebase DB', true, 'Firebase db is available')
        console.log('✅ Firebase db is available')
        
        // Test 1.5: Try to setup emulator for development
        if (this.nodeEnv === 'development') {
          try {
            const emulatorResult = setupFirebaseEmulator()
            if (emulatorResult) {
              this.addTestResult('Firebase Emulator', true, 'Connected to local Firebase Emulator')
              console.log('✅ Connected to Firebase Emulator')
            } else {
              this.addTestResult('Firebase Emulator', false, 'Emulator not available, using production')
              console.log('⚠️ Emulator not available, using production')
            }
          } catch (emulatorError) {
            this.addTestResult('Firebase Emulator', false, `Emulator error: ${emulatorError.message}`)
            console.log('⚠️ Emulator setup failed:', emulatorError.message)
          }
        }
        
        // Test 2: Test Authentication
        console.log('🔐 Testing Firebase Authentication...')
        const authStatus = checkAuthStatus()
        if (authStatus.isAuthenticated) {
          this.addTestResult('Firebase Auth', true, `User authenticated: ${authStatus.user.uid}`)
          console.log('✅ User already authenticated:', authStatus.user.uid)
        } else {
          console.log('🔐 User not authenticated, attempting anonymous auth...')
          const authResult = await authenticateAnonymously()
          if (authResult.success) {
            this.addTestResult('Firebase Auth', true, `Anonymous auth successful: ${authResult.user.uid}`)
            console.log('✅ Anonymous authentication successful:', authResult.user.uid)
          } else {
            this.addTestResult('Firebase Auth', false, `Auth failed: ${authResult.error}`)
            console.log('❌ Authentication failed:', authResult.error)
          }
        }
        
        // Test 3: Test Firebase Helper
        console.log('🔧 Testing Firebase Helper...')
        const helperTest = await FirebaseHelper.testConnection()
        if (helperTest.success) {
          this.addTestResult('Firebase Helper', true, helperTest.message)
          console.log('✅ Firebase Helper test successful')
        } else {
          this.addTestResult('Firebase Helper', false, helperTest.error)
          console.log('❌ Firebase Helper test failed:', helperTest.error)
        }
        
        // Test 4: Check Firestore functions
        const { collection, getDocs } = await import('firebase/firestore')
        this.addTestResult('Firestore Functions', true, 'Firestore functions imported successfully')
        console.log('✅ Firestore functions imported')
        
        // Test 5: Test collection reference
        const testRef = collection(db, 'tracking_driver')
        this.addTestResult('Collection Reference', true, 'Collection reference created successfully')
        console.log('✅ Collection reference created:', testRef)
        
        // Test 6: Test simple query with timeout
        try {
          console.log('🔍 Testing Firestore query...')
          
          // Add timeout to prevent hanging
          const queryPromise = getDocs(testRef)
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Query timeout after 10 seconds')), 10000)
          )
          
          const snapshot = await Promise.race([queryPromise, timeoutPromise])
          
          console.log('✅ Query executed successfully')
          console.log('📊 Snapshot size:', snapshot.size)
          console.log('📄 Snapshot empty:', snapshot.empty)
          
          this.addTestResult('Firestore Query', true, `Query executed successfully (${snapshot.size} documents)`)
          
          // Show first document if available
          if (!snapshot.empty) {
            const firstDoc = snapshot.docs[0]
            console.log('📋 First document ID:', firstDoc.id)
            console.log('📊 First document data:', firstDoc.data())
            
            // Test 7: Test specific tracking_driver collection
            try {
              console.log('🔍 Testing tracking_driver collection specifically...')
              const trackingRef = collection(db, 'tracking_driver')
              const trackingSnapshot = await getDocs(trackingRef)
              console.log('✅ Tracking collection query successful')
              console.log('📊 Tracking documents found:', trackingSnapshot.size)
              
              this.addTestResult('Tracking Collection', true, `Tracking collection accessible (${trackingSnapshot.size} documents)`)
              
              // Show all documents in tracking collection
              trackingSnapshot.forEach((doc, index) => {
                console.log(`📋 Document ${index + 1}:`, doc.id)
                console.log(`📊 Document ${index + 1} data:`, doc.data())
              })
              
            } catch (trackingError) {
              console.error('❌ Tracking collection error:', trackingError)
              console.error('❌ Error code:', trackingError.code)
              console.error('❌ Error message:', trackingError.message)
              
              if (trackingError.code === 'permission-denied') {
                this.addTestResult('Tracking Collection', false, 'Permission denied - check security rules')
              } else if (trackingError.code === 'unavailable') {
                this.addTestResult('Tracking Collection', false, 'Service unavailable - check network connection')
              } else {
                this.addTestResult('Tracking Collection', false, `Tracking collection error: ${trackingError.message}`)
              }
            }
          } else {
            console.log('⚠️ No documents found in test query')
            this.addTestResult('Tracking Collection', false, 'No documents found in tracking_driver collection')
          }
          
        } catch (queryError) {
          console.error('❌ Query failed:', queryError)
          console.error('❌ Error code:', queryError.code)
          console.error('❌ Error message:', queryError.message)
          
          // Check for specific error types
          if (queryError.code === 'permission-denied') {
            this.addTestResult('Firestore Query', false, 'Permission denied - check security rules')
          } else if (queryError.code === 'unavailable') {
            this.addTestResult('Firestore Query', false, 'Service unavailable - check network connection or Firebase project status')
          } else if (queryError.message.includes('timeout')) {
            this.addTestResult('Firestore Query', false, 'Query timeout - network or Firebase service issue')
          } else {
            this.addTestResult('Firestore Query', false, `Query failed: ${queryError.message}`)
          }
        }
        
        // Update connection status
        const successCount = this.testResults.filter(r => r.success).length
        const totalCount = this.testResults.length
        
        console.log(`📊 Test results: ${successCount}/${totalCount} successful`)
        
        if (successCount === totalCount) {
          this.connectionStatus = {
            type: 'success',
            icon: 'mdi-check-circle',
            message: `All tests passed (${successCount}/${totalCount})`
          }
        } else {
          this.connectionStatus = {
            type: 'warning',
            icon: 'mdi-alert',
            message: `Some tests failed (${successCount}/${totalCount})`
          }
        }
        
      } catch (error) {
        console.error('❌ General error in test connection:', error)
        this.addTestResult('General Error', false, `Unexpected error: ${error.message}`)
        this.connectionStatus = {
          type: 'error',
          icon: 'mdi-alert-circle',
          message: 'Connection test failed'
        }
      } finally {
        this.testing = false
        console.log('🏁 Connection test completed')
      }
    },
    
    async loadTrackingData() {
      this.loadingData = true
      this.addTestResult('Data Loading Start', true, 'Starting to load tracking data...')
      
      try {
        console.log('🔍 Starting to load tracking data directly with Firebase SDK...')
        
        // Import Firebase functions directly
        const { collection, getDocs } = await import('firebase/firestore')
        console.log('📚 Firestore functions imported')
        
        // Get Firebase db instance
        const { db, authenticateAnonymously, checkAuthStatus } = Firebase
        console.log('📡 Firebase db object:', db)
        console.log('📡 Firebase db type:', typeof db)
        console.log('📡 Firebase db name:', db?.name || 'unknown')
        
        if (!db) {
          throw new Error('Firebase db is not available')
        }
        
        // Check authentication first
        console.log('🔐 Checking authentication status...')
        const authStatus = checkAuthStatus()
        console.log('🔐 Auth status:', authStatus)
        
        if (!authStatus.isAuthenticated) {
          console.log('🔐 User not authenticated, attempting anonymous auth...')
          const authResult = await authenticateAnonymously()
          console.log('🔐 Auth result:', authResult)
          
          if (!authResult.success) {
            throw new Error(`Authentication failed: ${authResult.error}`)
          }
          console.log('✅ Authentication successful:', authResult.user.uid)
        } else {
          console.log('✅ User already authenticated:', authStatus.user.uid)
        }
        
        // Create collection reference
        const trackingRef = collection(db, 'tracking_driver')
        console.log('📂 Collection reference created:', trackingRef)
        console.log('📂 Collection path:', trackingRef.path)
        console.log('📂 Collection id:', trackingRef.id)
        
        // Execute query with timeout
        console.log('🔍 Executing query...')
        const queryPromise = getDocs(trackingRef)
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Query timeout after 15 seconds')), 15000)
        )
        
        console.log('⏳ Waiting for query result...')
        const snapshot = await Promise.race([queryPromise, timeoutPromise])
        console.log('✅ Query completed. Documents found:', snapshot.size)
        console.log('📄 Snapshot empty:', snapshot.empty)
        console.log('📄 Snapshot metadata:', snapshot.metadata)
        
        // Process documents
        this.trackingData = []
        let processedCount = 0
        
        // Log all documents first for debugging
        console.log('📋 All documents in snapshot:')
        snapshot.forEach((doc, index) => {
          console.log(`Document ${index + 1}:`, {
            id: doc.id,
            exists: doc.exists(),
            data: doc.data(),
            ref: doc.ref.path
          })
        })
        
        snapshot.forEach(doc => {
          console.log('📋 Processing document:', doc.id)
          const data = doc.data()
          console.log('📊 Raw document data:', data)
          
          // Handle different data types from Firebase
          const driverData = {
            id: doc.id,
            driver_id: this.extractValue(data.driver_id),
            driver_name: this.extractValue(data.driver_name),
            latitude: this.extractValue(data.latitude),
            longitude: this.extractValue(data.longitude),
            create_datetime: this.extractValue(data.create_datetime)
          }
          
          console.log('👤 Processed driver data:', driverData)
          this.trackingData.push(driverData)
          processedCount++
        })
        
        console.log('✅ Total processed drivers:', processedCount)
        console.log('📊 Final tracking data:', this.trackingData)
        
        this.addTestResult('Data Loading', true, `Successfully loaded ${this.trackingData.length} driver records`)
        
        // Update connection status
        this.connectionStatus = {
          type: 'success',
          icon: 'mdi-check-circle',
          message: `Data loaded successfully! Found ${this.trackingData.length} drivers`
        }
        
      } catch (error) {
        console.error('❌ Error loading tracking data:', error)
        console.error('❌ Error details:', {
          message: error.message,
          code: error.code,
          stack: error.stack,
          name: error.name
        })
        
        this.addTestResult('Data Loading', false, `Failed to load data: ${error.message}`)
        
        // Update connection status
        this.connectionStatus = {
          type: 'error',
          icon: 'mdi-alert-circle',
          message: `Failed to load data: ${error.message}`
        }
      } finally {
        this.loadingData = false
        console.log('🏁 Data loading process completed')
      }
    },
    
    async testRestAPI() {
      this.testingRest = true
      
      try {
        console.log('🌐 Testing Firebase REST API...')
        
        const url = `https://firestore.googleapis.com/v1/projects/megavin-2024/databases/megavin/documents/tracking_driver?key=AIzaSyBOPTtkIgtVgu2bMgCZ9aMsOKXblsegtdg`
        console.log('📡 Testing URL:', url)
        
        const response = await fetch(url)
        console.log('📊 Response status:', response.status)
        console.log('📊 Response headers:', response.headers)
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }
        
        const data = await response.json()
        console.log('📋 REST API Response:', data)
        
        if (data.documents && data.documents.length > 0) {
          console.log('✅ REST API found documents:', data.documents.length)
          
          // Parse the REST API data format
          const parsedData = data.documents.map(doc => {
            const fields = doc.fields || {}
            return {
              id: doc.name.split('/').pop(),
              driver_id: this.extractValue(fields.driver_id),
              driver_name: this.extractValue(fields.driver_name),
              latitude: this.extractValue(fields.latitude),
              longitude: this.extractValue(fields.longitude),
              create_datetime: this.extractValue(fields.create_datetime)
            }
          })
          
          console.log('👤 Parsed REST API data:', parsedData)
          
          // Update the tracking data with REST API results
          this.trackingData = parsedData
          
          this.addTestResult('REST API Test', true, `Successfully loaded ${parsedData.length} drivers via REST API`)
          
          this.connectionStatus = {
            type: 'success',
            icon: 'mdi-check-circle',
            message: `REST API successful! Found ${parsedData.length} drivers`
          }
        } else {
          console.log('⚠️ No documents found in REST API response')
          this.addTestResult('REST API Test', false, 'No documents found in REST API response')
        }
        
      } catch (error) {
        console.error('❌ REST API test failed:', error)
        this.addTestResult('REST API Test', false, `REST API failed: ${error.message}`)
        
        this.connectionStatus = {
          type: 'error',
          icon: 'mdi-alert-circle',
          message: `REST API failed: ${error.message}`
        }
      } finally {
        this.testingRest = false
      }
    },
    
    async compareSDKvsREST() {
      this.testingRest = true
      this.addTestResult('SDK vs REST Comparison', true, 'Comparing SDK and REST API results...')
      
      try {
        console.log('🔍 Comparing SDK vs REST API...')
        
        // Test REST API first
        const restUrl = 'https://firestore.googleapis.com/v1/projects/megavin-2024/databases/megavin/documents/tracking_driver'
        const restResponse = await fetch(restUrl)
        const restData = await restResponse.json()
        
        console.log('📋 REST API Response:', restData)
        console.log('📊 REST API documents found:', restData.documents?.length || 0)
        
        // Test SDK
        const { collection, getDocs } = await import('firebase/firestore')
        const { db, authenticateAnonymously, checkAuthStatus } = Firebase
        
        // Ensure authentication
        const authStatus = checkAuthStatus()
        if (!authStatus.isAuthenticated) {
          await authenticateAnonymously()
        }
        
        const trackingRef = collection(db, 'tracking_driver')
        const snapshot = await getDocs(trackingRef)
        
        console.log('📊 SDK documents found:', snapshot.size)
        
        // Compare results
        const restCount = restData.documents?.length || 0
        const sdkCount = snapshot.size
        
        console.log(`📊 Comparison: REST API found ${restCount} documents, SDK found ${sdkCount} documents`)
        
        if (restCount === sdkCount) {
          this.addTestResult('SDK vs REST Comparison', true, `Both methods found ${restCount} documents`)
        } else {
          this.addTestResult('SDK vs REST Comparison', false, `REST API found ${restCount}, SDK found ${sdkCount} documents`)
        }
        
        // Show details
        if (restData.documents && restData.documents.length > 0) {
          console.log('📋 REST API documents:')
          restData.documents.forEach((doc, index) => {
            console.log(`Document ${index + 1}:`, doc.name.split('/').pop())
          })
        }
        
        if (!snapshot.empty) {
          console.log('📋 SDK documents:')
          snapshot.docs.forEach((doc, index) => {
            console.log(`Document ${index + 1}:`, doc.id)
          })
        }
        
      } catch (error) {
        console.error('❌ SDK vs REST comparison failed:', error)
        this.addTestResult('SDK vs REST Comparison', false, `Comparison failed: ${error.message}`)
      } finally {
        this.testingRest = false
      }
    },
    
    async testNetworkStatus() {
      this.testingNetwork = true
      this.addTestResult('Network Status Test', true, 'Starting network status test...')
      
      try {
        console.log('🌐 Testing network connectivity...')
        
        // Test 1: Check if Firebase is available
        const { db } = Firebase
        if (!db) {
          throw new Error('Firebase db is not available. Cannot test network.')
        }
        this.addTestResult('Network Status', true, 'Firebase db is available. Proceeding with network test.')
        
        // Test 2: Try to access Firebase directly instead of external URL
        const url = 'https://firestore.googleapis.com/v1/projects/megavin-2024/databases/megavin/documents/tracking_driver'
        console.log('📡 Testing Firebase URL:', url)
        
        try {
          const response = await fetch(url)
          console.log('📊 Response status:', response.status)
          
          if (response.ok) {
            this.addTestResult('Network Status', true, `Firebase is reachable. Response status: ${response.status}`)
            this.connectionStatus = {
              type: 'success',
              icon: 'mdi-check-circle',
              message: `Firebase is reachable! Response status: ${response.status}`
            }
          } else {
            this.addTestResult('Network Status', false, `Firebase not reachable. Response status: ${response.status}`)
            this.connectionStatus = {
              type: 'error',
              icon: 'mdi-alert-circle',
              message: `Firebase not reachable. Response status: ${response.status}`
            }
          }
        } catch (fetchError) {
          console.log('⚠️ Fetch error (this might be due to CORS):', fetchError.message)
          
          // Fallback: Test if we can at least make a request (even if blocked by CORS)
          this.addTestResult('Network Status', true, 'Network connectivity detected (CORS may block external requests)')
          this.connectionStatus = {
            type: 'info',
            icon: 'mdi-information',
            message: 'Network connectivity detected. CORS may block external requests in development.'
          }
        }
        
      } catch (error) {
        console.error('❌ Network status test failed:', error)
        this.addTestResult('Network Status', false, `Network test failed: ${error.message}`)
        this.connectionStatus = {
          type: 'error',
          icon: 'mdi-alert-circle',
          message: `Network test failed: ${error.message}`
        }
      } finally {
        this.testingNetwork = false
      }
    },

    async simpleFirebaseTest() {
      this.testingSimple = true
      this.testResults = []
      this.addTestResult('Simple Firebase Test', true, 'Starting simple Firebase test...')

      try {
        console.log('🧪 Simple Firebase Test...')

        // Test 1: Check if Firebase is available
        const { db } = Firebase
        if (!db) {
          throw new Error('Firebase db is not available. Cannot perform simple test.')
        }
        this.addTestResult('Simple Firebase Test', true, 'Firebase db is available. Proceeding with simple test.')

        // Test 2: Try to access a collection directly
        const { collection, getDocs } = await import('firebase/firestore')
        const testRef = collection(db, 'tracking_driver')
        this.addTestResult('Simple Collection Access', true, 'Attempting to access tracking_driver collection...')

        try {
          const snapshot = await getDocs(testRef)
          console.log('✅ Simple collection access successful. Documents found:', snapshot.size)
          this.addTestResult('Simple Collection Access', true, `Simple collection accessible (${snapshot.size} documents)`)

          if (snapshot.empty) {
            this.addTestResult('Simple Collection Access', false, 'No documents found in tracking_driver collection.')
            console.log('⚠️ No documents found in tracking_driver collection')
            
            // Try to check if collection exists by trying to get a non-existent document
            try {
              const { doc, getDoc } = await import('firebase/firestore')
              const testDocRef = doc(db, 'tracking_driver', 'test-doc-that-does-not-exist')
              const testDoc = await getDoc(testDocRef)
              console.log('✅ Collection exists but is empty')
              this.addTestResult('Collection Exists', true, 'Collection exists but is empty')
            } catch (docError) {
              console.log('❌ Collection might not exist or have permission issues')
              this.addTestResult('Collection Exists', false, 'Collection might not exist or have permission issues')
            }
          } else {
            this.addTestResult('Simple Collection Access', true, `Found ${snapshot.size} documents in tracking_driver collection.`)
            console.log('✅ Found documents in collection')
            
            // Show first few documents
            snapshot.docs.slice(0, 3).forEach((doc, index) => {
              console.log(`📋 Document ${index + 1}:`, doc.id)
              console.log(`📊 Document ${index + 1} data:`, doc.data())
            })
          }
        } catch (error) {
          console.error('❌ Simple collection access failed:', error)
          console.error('❌ Error code:', error.code)
          console.error('❌ Error message:', error.message)

          if (error.code === 'permission-denied') {
            this.addTestResult('Simple Collection Access', false, 'Permission denied - check security rules.')
          } else if (error.code === 'unavailable') {
            this.addTestResult('Simple Collection Access', false, 'Service unavailable - check network connection or Firebase project status.')
          } else {
            this.addTestResult('Simple Collection Access', false, `Simple collection error: ${error.message}`)
          }
        }

      } catch (error) {
        console.error('❌ General error in simple Firebase test:', error)
        this.addTestResult('Simple Firebase Test', false, `Unexpected error: ${error.message}`)
        this.connectionStatus = {
          type: 'error',
          icon: 'mdi-alert-circle',
          message: `Simple Firebase test failed: ${error.message}`
        }
      } finally {
        this.testingSimple = false
        console.log('🏁 Simple Firebase test completed')
      }
    },

    async testDatabaseConfig() {
      this.testingConfig = true
      this.testResults = []
      this.addTestResult('Database Config Test', true, 'Starting database configuration test...')

      try {
        console.log('🔧 Testing Firebase Database Configuration...')

        const { db, setupFirebaseEmulator, authenticateAnonymously, checkAuthStatus } = Firebase
        console.log('📡 Firebase db object:', db)
        console.log('📡 Firebase db type:', typeof db)
        console.log('📡 Firebase db name:', db?.name || 'unknown')

        if (!db) {
          throw new Error('Firebase db is not available. Cannot test database configuration.')
        }
        this.addTestResult('Database Config', true, 'Firebase db is available. Proceeding with config test.')

        // Test 1: Check if we can access the app
        const { firebaseApp } = Firebase
        console.log('🔥 Firebase app:', firebaseApp)
        console.log('⚙️ App options:', firebaseApp.options)

        // Test 2: Try to setup emulator for development
        if (this.nodeEnv === 'development') {
          try {
            const emulatorResult = setupFirebaseEmulator()
            if (emulatorResult) {
              this.addTestResult('Firebase Emulator', true, 'Connected to local Firebase Emulator')
              console.log('✅ Connected to Firebase Emulator')
            } else {
              this.addTestResult('Firebase Emulator', false, 'Emulator not available, using production')
              console.log('⚠️ Emulator not available, using production')
            }
          } catch (emulatorError) {
            this.addTestResult('Firebase Emulator', false, `Emulator error: ${emulatorError.message}`)
            console.log('⚠️ Emulator setup failed:', emulatorError.message)
          }
        }

        // Test 3: Test Authentication
        console.log('🔐 Testing Firebase Authentication...')
        const authStatus = checkAuthStatus()
        if (authStatus.isAuthenticated) {
          this.addTestResult('Firebase Auth', true, `User authenticated: ${authStatus.user.uid}`)
          console.log('✅ User already authenticated:', authStatus.user.uid)
        } else {
          console.log('🔐 User not authenticated, attempting anonymous auth...')
          const authResult = await authenticateAnonymously()
          if (authResult.success) {
            this.addTestResult('Firebase Auth', true, `Anonymous auth successful: ${authResult.user.uid}`)
            console.log('✅ Anonymous authentication successful:', authResult.user.uid)
          } else {
            this.addTestResult('Firebase Auth', false, `Auth failed: ${authResult.error}`)
            console.log('❌ Authentication failed:', authResult.error)
          }
        }

        // Test 4: Check if we can access Firestore functions
        const { collection, getDocs } = await import('firebase/firestore')
        this.addTestResult('Firestore Functions', true, 'Firestore functions imported successfully')
        console.log('✅ Firestore functions imported')

        // Test 5: Test collection reference
        const testRef = collection(db, 'tracking_driver')
        this.addTestResult('Collection Reference', true, 'Collection reference created successfully')
        console.log('✅ Collection reference created:', testRef)

        // Test 6: Test simple query with timeout
        try {
          console.log('🔍 Testing Firestore query...')
          
          // Add timeout to prevent hanging
          const queryPromise = getDocs(testRef)
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Query timeout after 10 seconds')), 10000)
          )
          
          const snapshot = await Promise.race([queryPromise, timeoutPromise])
          
          console.log('✅ Query executed successfully')
          console.log('📊 Snapshot size:', snapshot.size)
          console.log('📄 Snapshot empty:', snapshot.empty)
          
          this.addTestResult('Firestore Query', true, `Query executed successfully (${snapshot.size} documents)`)
          
          // Show first document if available
          if (!snapshot.empty) {
            const firstDoc = snapshot.docs[0]
            console.log('📋 First document ID:', firstDoc.id)
            console.log('📊 First document data:', firstDoc.data())
            
            // Test 7: Test specific tracking_driver collection
            try {
              console.log('🔍 Testing tracking_driver collection specifically...')
              const trackingRef = collection(db, 'tracking_driver')
              const trackingSnapshot = await getDocs(trackingRef)
              console.log('✅ Tracking collection query successful')
              console.log('📊 Tracking documents found:', trackingSnapshot.size)
              
              this.addTestResult('Tracking Collection', true, `Tracking collection accessible (${trackingSnapshot.size} documents)`)
              
              // Show all documents in tracking collection
              trackingSnapshot.forEach((doc, index) => {
                console.log(`📋 Document ${index + 1}:`, doc.id)
                console.log(`📊 Document ${index + 1} data:`, doc.data())
              })
              
            } catch (trackingError) {
              console.error('❌ Tracking collection error:', trackingError)
              console.error('❌ Error code:', trackingError.code)
              console.error('❌ Error message:', trackingError.message)
              
              if (trackingError.code === 'permission-denied') {
                this.addTestResult('Tracking Collection', false, 'Permission denied - check security rules')
              } else if (trackingError.code === 'unavailable') {
                this.addTestResult('Tracking Collection', false, 'Service unavailable - check network connection')
              } else {
                this.addTestResult('Tracking Collection', false, `Tracking collection error: ${trackingError.message}`)
              }
            }
          } else {
            console.log('⚠️ No documents found in test query')
            this.addTestResult('Tracking Collection', false, 'No documents found in tracking_driver collection')
          }
          
        } catch (queryError) {
          console.error('❌ Query failed:', queryError)
          console.error('❌ Error code:', queryError.code)
          console.error('❌ Error message:', queryError.message)
          
          // Check for specific error types
          if (queryError.code === 'permission-denied') {
            this.addTestResult('Firestore Query', false, 'Permission denied - check security rules')
          } else if (queryError.code === 'unavailable') {
            this.addTestResult('Firestore Query', false, 'Service unavailable - check network connection or Firebase project status')
          } else if (queryError.message.includes('timeout')) {
            this.addTestResult('Firestore Query', false, 'Query timeout - network or Firebase service issue')
          } else {
            this.addTestResult('Firestore Query', false, `Query failed: ${queryError.message}`)
          }
        }

        this.addTestResult('Database Config', true, 'Database configuration test successful!')
        this.connectionStatus = {
          type: 'success',
          icon: 'mdi-check-circle',
          message: 'Database configuration test passed!'
        }

      } catch (error) {
        console.error('❌ General error in database config test:', error)
        this.addTestResult('Database Config', false, `Unexpected error: ${error.message}`)
        this.connectionStatus = {
          type: 'error',
          icon: 'mdi-alert-circle',
          message: `Database config test failed: ${error.message}`
        }
      } finally {
        this.testingConfig = false
        console.log('🏁 Database config test completed')
      }
    },
    
    async testExactDataComparison() {
      this.testingExact = true
      this.addTestResult('Exact Data Comparison', true, 'Comparing exact data from REST API vs SDK...')
      
      try {
        console.log('🔍 Testing exact data comparison...')
        
        // Step 1: Get data from REST API
        console.log('📡 Step 1: Getting data from REST API...')
        const restUrl = 'https://firestore.googleapis.com/v1/projects/megavin-2024/databases/megavin/documents/tracking_driver'
        const restResponse = await fetch(restUrl)
        const restData = await restResponse.json()
        
        console.log('📋 REST API Raw Response:', restData)
        console.log('📊 REST API documents count:', restData.documents?.length || 0)
        
        // Parse REST API data
        const restParsedData = []
        if (restData.documents && restData.documents.length > 0) {
          restData.documents.forEach((doc, index) => {
            const fields = doc.fields || {}
            const parsedDoc = {
              id: doc.name.split('/').pop(),
              driver_id: this.extractValue(fields.driver_id),
              driver_name: this.extractValue(fields.driver_name),
              latitude: this.extractValue(fields.latitude),
              longitude: this.extractValue(fields.longitude),
              create_datetime: this.extractValue(fields.create_datetime)
            }
            restParsedData.push(parsedDoc)
            console.log(`📋 REST Document ${index + 1}:`, parsedDoc)
          })
        }
        
        this.addTestResult('REST API Data', true, `REST API found ${restParsedData.length} documents`)
        
        // Step 2: Get data from SDK
        console.log('📡 Step 2: Getting data from SDK...')
        const { collection, getDocs } = await import('firebase/firestore')
        const { db, authenticateAnonymously, checkAuthStatus } = Firebase
        
        // Ensure authentication
        const authStatus = checkAuthStatus()
        if (!authStatus.isAuthenticated) {
          console.log('🔐 Authenticating for SDK...')
          const authResult = await authenticateAnonymously()
          if (!authResult.success) {
            throw new Error(`SDK Authentication failed: ${authResult.error}`)
          }
          console.log('✅ SDK Authentication successful')
        } else {
          console.log('✅ SDK already authenticated')
        }
        
        // Create collection reference
        const trackingRef = collection(db, 'tracking_driver')
        console.log('📂 SDK Collection reference:', trackingRef)
        console.log('📂 SDK Collection path:', trackingRef.path)
        console.log('📂 SDK Database name:', db.name)
        
        // Execute SDK query
        console.log('🔍 Executing SDK query...')
        const snapshot = await getDocs(trackingRef)
        console.log('✅ SDK Query completed')
        console.log('📊 SDK Snapshot size:', snapshot.size)
        console.log('📄 SDK Snapshot empty:', snapshot.empty)
        
        // Parse SDK data
        const sdkParsedData = []
        if (!snapshot.empty) {
          snapshot.forEach((doc, index) => {
            const data = doc.data()
            const parsedDoc = {
              id: doc.id,
              driver_id: this.extractValue(data.driver_id),
              driver_name: this.extractValue(data.driver_name),
              latitude: this.extractValue(data.latitude),
              longitude: this.extractValue(data.longitude),
              create_datetime: this.extractValue(data.create_datetime)
            }
            sdkParsedData.push(parsedDoc)
            console.log(`📋 SDK Document ${index + 1}:`, parsedDoc)
            console.log(`📊 SDK Raw data ${index + 1}:`, data)
          })
        }
        
        this.addTestResult('SDK Data', true, `SDK found ${sdkParsedData.length} documents`)
        
        // Step 3: Compare results
        console.log('📊 Step 3: Comparing results...')
        console.log(`📊 REST API: ${restParsedData.length} documents`)
        console.log(`📊 SDK: ${sdkParsedData.length} documents`)
        
        // Show detailed comparison
        console.log('📋 REST API Documents:')
        restParsedData.forEach((doc, index) => {
          console.log(`  Document ${index + 1}:`, doc)
        })
        
        console.log('📋 SDK Documents:')
        sdkParsedData.forEach((doc, index) => {
          console.log(`  Document ${index + 1}:`, doc)
        })
        
        // Check if data matches
        if (restParsedData.length === sdkParsedData.length) {
          this.addTestResult('Data Count Match', true, `Both methods found ${restParsedData.length} documents`)
          
          // Check if content matches
          const contentMatches = JSON.stringify(restParsedData.sort((a, b) => a.id.localeCompare(b.id))) === 
                                JSON.stringify(sdkParsedData.sort((a, b) => a.id.localeCompare(b.id)))
          
          if (contentMatches) {
            this.addTestResult('Data Content Match', true, 'Data content matches between REST API and SDK')
            console.log('✅ Data content matches between REST API and SDK')
          } else {
            this.addTestResult('Data Content Match', false, 'Data content differs between REST API and SDK')
            console.log('❌ Data content differs between REST API and SDK')
          }
        } else {
          this.addTestResult('Data Count Match', false, `REST API: ${restParsedData.length}, SDK: ${sdkParsedData.length}`)
          console.log('❌ Document count differs between REST API and SDK')
        }
        
        // Update tracking data with REST API results (since we know it works)
        if (restParsedData.length > 0) {
          this.trackingData = restParsedData
          this.addTestResult('Data Loading', true, `Loaded ${restParsedData.length} drivers from REST API`)
          
          this.connectionStatus = {
            type: 'success',
            icon: 'mdi-check-circle',
            message: `Data loaded successfully! Found ${restParsedData.length} drivers via REST API`
          }
        }
        
        // Summary
        console.log('📊 Summary:')
        console.log(`  REST API: ${restParsedData.length} documents`)
        console.log(`  SDK: ${sdkParsedData.length} documents`)
        console.log(`  Database: ${db.name}`)
        console.log(`  Collection: tracking_driver`)
        console.log(`  Project: megavin-2024`)
        
      } catch (error) {
        console.error('❌ Exact data comparison failed:', error)
        this.addTestResult('Exact Data Comparison', false, `Comparison failed: ${error.message}`)
        
        this.connectionStatus = {
          type: 'error',
          icon: 'mdi-alert-circle',
          message: `Data comparison failed: ${error.message}`
        }
      } finally {
        this.testingExact = false
        console.log('🏁 Exact data comparison completed')
      }
    },
    
    // Helper method to extract values from Firebase data structure
    extractValue(field) {
      if (!field) return 'N/A'
      
      // Handle Firebase data types
      if (typeof field === 'object') {
        if (field.stringValue) return field.stringValue
        if (field.integerValue) return field.integerValue.toString()
        if (field.doubleValue) return field.doubleValue.toString()
        if (field.booleanValue !== undefined) return field.booleanValue.toString()
        if (field.timestampValue) return field.timestampValue
        if (field.nullValue !== undefined) return 'N/A'
      }
      
      // Handle direct values
      if (typeof field === 'string') return field
      if (typeof field === 'number') return field.toString()
      if (typeof field === 'boolean') return field.toString()
      
      return 'N/A'
    },
    
    viewDriverDetails(driver) {
      this.selectedDriver = driver
      this.showDriverDialog = true
    },
    
    addTestResult(title, success, message) {
      this.testResults.push({
        title,
        success,
        message
      })
    },

    clearResults() {
      this.testResults = [];
      this.trackingData = [];
      this.connectionStatus = {
        type: 'info',
        icon: 'mdi-information',
        message: 'Click "Test Connection" to check Firebase status'
      };
      this.debugInfo = {
        projectId: 'Loading...',
        dbStatus: 'Unknown',
        lastTestTime: 'Not tested yet'
      };
      console.log('🧹 Cleared all test results and data.');
    },

    async testSDKWithDifferentConfigs() {
      this.testingExact = true
      this.addTestResult('SDK Config Test', true, 'Testing SDK with different configurations...')
      
      try {
        console.log('🔧 Testing SDK with different configurations...')
        
        // Test 1: Check current Firebase configuration
        const { firebaseApp, db } = Firebase
        console.log('🔥 Firebase App:', firebaseApp)
        console.log('📡 Current Database:', db)
        console.log('📊 Database name:', db?.name)
        console.log('📊 Database type:', typeof db)
        
        // Test 2: Try with default database
        console.log('🔍 Test 2: Trying with default database...')
        try {
          const { getFirestore, collection, getDocs } = await import('firebase/firestore')
          const defaultDb = getFirestore(firebaseApp) // No database name = default
          console.log('📡 Default database:', defaultDb)
          console.log('📊 Default database name:', defaultDb.name)
          
          const defaultRef = collection(defaultDb, 'tracking_driver')
          const defaultSnapshot = await getDocs(defaultRef)
          console.log('📊 Default DB documents found:', defaultSnapshot.size)
          
          this.addTestResult('Default Database', true, `Default DB found ${defaultSnapshot.size} documents`)
        } catch (defaultError) {
          console.error('❌ Default database error:', defaultError)
          this.addTestResult('Default Database', false, `Default DB error: ${defaultError.message}`)
        }
        
        // Test 3: Try with explicit megavin database
        console.log('🔍 Test 3: Trying with explicit megavin database...')
        try {
          const { getFirestore, collection, getDocs } = await import('firebase/firestore')
          const megavinDb = getFirestore(firebaseApp, 'megavin')
          console.log('📡 Megavin database:', megavinDb)
          console.log('📊 Megavin database name:', megavinDb.name)
          
          const megavinRef = collection(megavinDb, 'tracking_driver')
          const megavinSnapshot = await getDocs(megavinRef)
          console.log('📊 Megavin DB documents found:', megavinSnapshot.size)
          
          this.addTestResult('Megavin Database', true, `Megavin DB found ${megavinSnapshot.size} documents`)
          
          // Show documents if found
          if (!megavinSnapshot.empty) {
            megavinSnapshot.forEach((doc, index) => {
              console.log(`📋 Megavin Document ${index + 1}:`, doc.id)
              console.log(`📊 Megavin Document ${index + 1} data:`, doc.data())
            })
          }
        } catch (megavinError) {
          console.error('❌ Megavin database error:', megavinError)
          this.addTestResult('Megavin Database', false, `Megavin DB error: ${megavinError.message}`)
        }
        
        // Test 4: Try with authentication first
        console.log('🔍 Test 4: Trying with authentication...')
        try {
          const { db, authenticateAnonymously, checkAuthStatus } = Firebase
          const { collection, getDocs } = await import('firebase/firestore')
          
          // Check current auth status
          const authStatus = checkAuthStatus()
          console.log('🔐 Current auth status:', authStatus)
          
          if (!authStatus.isAuthenticated) {
            console.log('🔐 Attempting anonymous authentication...')
            const authResult = await authenticateAnonymously()
            console.log('🔐 Auth result:', authResult)
            
            if (authResult.success) {
              console.log('✅ Authentication successful:', authResult.user.uid)
              
              // Now try to get data
              const authRef = collection(db, 'tracking_driver')
              const authSnapshot = await getDocs(authRef)
              console.log('📊 Authenticated query documents found:', authSnapshot.size)
              
              this.addTestResult('Authenticated Query', true, `Authenticated query found ${authSnapshot.size} documents`)
              
              // Show documents if found
              if (!authSnapshot.empty) {
                authSnapshot.forEach((doc, index) => {
                  console.log(`📋 Auth Document ${index + 1}:`, doc.id)
                  console.log(`📊 Auth Document ${index + 1} data:`, doc.data())
                })
              }
            } else {
              this.addTestResult('Authentication', false, `Auth failed: ${authResult.error}`)
            }
          } else {
            console.log('✅ Already authenticated:', authStatus.user.uid)
            
            // Try to get data with existing auth
            const authRef = collection(db, 'tracking_driver')
            const authSnapshot = await getDocs(authRef)
            console.log('📊 Existing auth query documents found:', authSnapshot.size)
            
            this.addTestResult('Existing Auth Query', true, `Existing auth query found ${authSnapshot.size} documents`)
          }
        } catch (authError) {
          console.error('❌ Authentication test error:', authError)
          this.addTestResult('Authentication Test', false, `Auth test error: ${authError.message}`)
        }
        
        // Test 5: Try with different collection names
        console.log('🔍 Test 5: Trying different collection names...')
        try {
          const { db } = Firebase
          const { collection, getDocs } = await import('firebase/firestore')
          
          const collections = ['tracking_driver', 'tracking_drivers', 'driver_tracking', 'drivers']
          
          for (const collectionName of collections) {
            try {
              console.log(`🔍 Testing collection: ${collectionName}`)
              const testRef = collection(db, collectionName)
              const testSnapshot = await getDocs(testRef)
              console.log(`📊 ${collectionName} documents found:`, testSnapshot.size)
              
              if (!testSnapshot.empty) {
                console.log(`✅ Found data in ${collectionName}!`)
                this.addTestResult(`Collection ${collectionName}`, true, `Found ${testSnapshot.size} documents`)
                break
              } else {
                this.addTestResult(`Collection ${collectionName}`, false, 'No documents found')
              }
            } catch (collectionError) {
              console.error(`❌ ${collectionName} error:`, collectionError)
              this.addTestResult(`Collection ${collectionName}`, false, `Error: ${collectionError.message}`)
            }
          }
        } catch (collectionTestError) {
          console.error('❌ Collection test error:', collectionTestError)
          this.addTestResult('Collection Test', false, `Collection test error: ${collectionTestError.message}`)
        }
        
        // Test 6: Check if we can access any collection
        console.log('🔍 Test 6: Checking if we can access any collection...')
        try {
          const { db } = Firebase
          const { collection, getDocs } = await import('firebase/firestore')
          
          // Try a simple test collection
          const testRef = collection(db, 'test')
          const testSnapshot = await getDocs(testRef)
          console.log('📊 Test collection documents found:', testSnapshot.size)
          
          this.addTestResult('Test Collection', true, `Test collection accessible (${testSnapshot.size} documents)`)
        } catch (testError) {
          console.error('❌ Test collection error:', testError)
          this.addTestResult('Test Collection', false, `Test collection error: ${testError.message}`)
        }
        
        // Summary
        console.log('📊 SDK Configuration Test Summary:')
        console.log('  - Current database:', db?.name)
        console.log('  - Project ID:', firebaseApp.options.projectId)
        console.log('  - Auth domain:', firebaseApp.options.authDomain)
        
      } catch (error) {
        console.error('❌ SDK configuration test failed:', error)
        this.addTestResult('SDK Config Test', false, `Config test failed: ${error.message}`)
        
        this.connectionStatus = {
          type: 'error',
          icon: 'mdi-alert-circle',
          message: `SDK config test failed: ${error.message}`
        }
      } finally {
        this.testingExact = false
        console.log('🏁 SDK configuration test completed')
      }
    },
    async testRESTvsSDKComparison() {
      this.testingExact = true
      this.addTestResult('REST vs SDK Test', true, 'Testing REST API vs SDK comparison...')
      
      try {
        console.log('🔍 Testing REST API vs SDK comparison...')
        
        // Step 1: Test REST API first (we know this works)
        console.log('📡 Step 1: Testing REST API...')
        const restUrl = 'https://firestore.googleapis.com/v1/projects/megavin-2024/databases/megavin/documents/tracking_driver'
        
        const restResponse = await fetch(restUrl)
        const restData = await restResponse.json()
        
        console.log('📋 REST API Response:', restData)
        console.log('📊 REST API documents count:', restData.documents?.length || 0)
        
        if (restData.documents && restData.documents.length > 0) {
          console.log('✅ REST API found data!')
          restData.documents.forEach((doc, index) => {
            console.log(`📋 REST Document ${index + 1}:`, doc.name.split('/').pop())
            console.log(`📊 REST Document ${index + 1} fields:`, doc.fields)
          })
          
          this.addTestResult('REST API', true, `REST API found ${restData.documents.length} documents`)
        } else {
          console.log('❌ REST API found no documents')
          this.addTestResult('REST API', false, 'REST API found no documents')
        }
        
        // Step 2: Test SDK with same data
        console.log('📡 Step 2: Testing SDK...')
        const { collection, getDocs } = await import('firebase/firestore')
        const { db, authenticateAnonymously, checkAuthStatus } = Firebase
        
        console.log('📊 SDK Database name:', db?.name)
        console.log('📊 SDK Database type:', typeof db)
        
        // Ensure authentication
        const authStatus = checkAuthStatus()
        console.log('🔐 Auth status:', authStatus)
        
        if (!authStatus.isAuthenticated) {
          console.log('🔐 Attempting authentication...')
          const authResult = await authenticateAnonymously()
          console.log('🔐 Auth result:', authResult)
          
          if (!authResult.success) {
            throw new Error(`SDK Authentication failed: ${authResult.error}`)
          }
        }
        
        // Try to get data with SDK
        const trackingRef = collection(db, 'tracking_driver')
        console.log('📂 SDK Collection reference:', trackingRef)
        console.log('📂 SDK Collection path:', trackingRef.path)
        
        const snapshot = await getDocs(trackingRef)
        console.log('📊 SDK Snapshot size:', snapshot.size)
        console.log('📄 SDK Snapshot empty:', snapshot.empty)
        
        if (!snapshot.empty) {
          console.log('✅ SDK found data!')
          snapshot.forEach((doc, index) => {
            console.log(`📋 SDK Document ${index + 1}:`, doc.id)
            console.log(`📊 SDK Document ${index + 1} data:`, doc.data())
          })
          
          this.addTestResult('SDK', true, `SDK found ${snapshot.size} documents`)
        } else {
          console.log('❌ SDK found no documents')
          this.addTestResult('SDK', false, 'SDK found no documents')
        }
        
        // Step 3: Compare results
        const restCount = restData.documents?.length || 0
        const sdkCount = snapshot.size
        
        console.log('📊 Comparison Results:')
        console.log(`  REST API: ${restCount} documents`)
        console.log(`  SDK: ${sdkCount} documents`)
        console.log(`  Database: ${db.name}`)
        console.log(`  Project: megavin-2024`)
        
        if (restCount === sdkCount) {
          this.addTestResult('Data Count Match', true, `Both methods found ${restCount} documents`)
        } else {
          this.addTestResult('Data Count Match', false, `REST API: ${restCount}, SDK: ${sdkCount}`)
        }
        
        // Step 4: Try to load data into UI
        if (restCount > 0) {
          // Parse REST API data for display
          const parsedData = restData.documents.map(doc => {
            const fields = doc.fields || {}
            return {
              id: doc.name.split('/').pop(),
              driver_id: this.extractValue(fields.driver_id),
              driver_name: this.extractValue(fields.driver_name),
              latitude: this.extractValue(fields.latitude),
              longitude: this.extractValue(fields.longitude),
              create_datetime: this.extractValue(fields.create_datetime)
            }
          })
          
          this.trackingData = parsedData
          this.addTestResult('Data Loading', true, `Loaded ${parsedData.length} drivers from REST API`)
          
          this.connectionStatus = {
            type: 'success',
            icon: 'mdi-check-circle',
            message: `Data loaded successfully! Found ${parsedData.length} drivers via REST API`
          }
        }
        
        // Step 5: Show detailed error if SDK failed
        if (restCount > 0 && sdkCount === 0) {
          console.log('⚠️ REST API has data but SDK doesn\'t. Possible issues:')
          console.log('  1. Database name mismatch')
          console.log('  2. Authentication issues')
          console.log('  3. Security rules blocking SDK')
          console.log('  4. Collection path issues')
          
          this.addTestResult('SDK Issue', false, 'SDK cannot access data that REST API can access')
        }
        
      } catch (error) {
        console.error('❌ REST vs SDK comparison failed:', error)
        this.addTestResult('REST vs SDK Test', false, `Comparison failed: ${error.message}`)
        
        this.connectionStatus = {
          type: 'error',
          icon: 'mdi-alert-circle',
          message: `Comparison failed: ${error.message}`
        }
      } finally {
        this.testingExact = false
        console.log('🏁 REST vs SDK comparison completed')
      }
    }
  }
}
</script> 